/**
 * A station shown by name (SA-1031 spec §7.2, D12). Full: a chip, the code and
 * the display name, "City, Country" as the tooltip. Compact: the code alone
 * with the same words as the tooltip. Unresolved (no code in the text, no row
 * for it, or still loading): the text as typed, no tooltip, no chip — a reader
 * can never tell a slow lookup from a free-text place, and nothing is refused
 * on a read-only surface. The caller resolves (`useAirportLookup`) and hands
 * the row in, so a list renders from one lookup. A row with no display name
 * (a mocked or malformed read) is unresolved too, never printed as "undefined".
 */
import { Badge } from '@/components/ui'
import { cn } from '@/utils'
import type { StationWords } from './place'
import { stationCode } from './stationCode'

export interface StationProps {
  /** The stored value, as typed. */
  text: string
  /** The lookup's answer for `stationCode(text)`, if any; the stored words of a resolved place (SA-1051) fit too. */
  airport?: StationWords | null
  compact?: boolean
  /** Merged onto the chip after its variant classes, so a caller's tone wins. */
  className?: string
  /** The chip's tooltip; the place words when omitted. */
  title?: string
}

export function Station({ text, airport, compact = false, className, title }: StationProps) {
  const code = stationCode(text)
  if (!code || !airport?.displayName) return <>{text}</>
  if (compact) {
    return <span className="font-mono" title={`${airport.displayName} · ${airport.placeWords}`}>{code}</span>
  }
  return (
    <Badge variant="neutral" title={title ?? airport.placeWords} className={cn('max-w-full gap-1.5 font-normal', className)}>
      <span className="font-mono font-semibold">{code}</span>
      {' '}
      <span className="min-w-0 truncate text-muted-foreground">{airport.displayName}</span>
      <span className="sr-only select-none">, {airport.placeWords}</span>
    </Badge>
  )
}
